import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
  App as AntApp,
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Switch,
  Tag,
  Table,
} from 'antd';
import { useState } from 'react';
import { useDeleteErrorRule, useErrorRules, useSaveErrorRule } from '../../api/hooks';
import type { ProviderErrorRule } from '../../api/types';
import { extractErrorMessage } from '../../api/client';
import { ErrorClassTag } from '../../utils/status';

const CLASSIFY_OPTIONS = [
  { value: 'source', label: 'Lỗi nguồn' },
  { value: 'user', label: 'Lỗi người dùng' },
  { value: 'retry', label: 'Thử lại' },
  { value: 'fatal', label: 'Nghiêm trọng' },
];

export default function ErrorRulesTab({ providerId }: { providerId: string }) {
  const { message } = AntApp.useApp();
  const rules = useErrorRules(providerId);
  const save = useSaveErrorRule(providerId);
  const del = useDeleteErrorRule(providerId);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ProviderErrorRule | null>(null);
  const [form] = Form.useForm();

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ classify: 'retry', noRetry: false, priority: 1, enabled: true });
    setOpen(true);
  };

  const openEdit = (r: ProviderErrorRule) => {
    setEditing(r);
    form.setFieldsValue({ ...r, matchStatus: (r.matchStatus ?? []).join(', ') });
    setOpen(true);
  };

  const onSubmit = async () => {
    const v = await form.validateFields();
    const matchStatus = String(v.matchStatus ?? '')
      .split(',')
      .map((s: string) => Number(s.trim()))
      .filter((n: number) => !Number.isNaN(n));
    const data: Partial<ProviderErrorRule> = {
      name: v.name,
      matchStatus,
      matchBodyContains: v.matchBodyContains,
      classify: v.classify,
      noRetry: Boolean(v.noRetry),
      priority: v.priority,
      enabled: v.enabled,
    };
    try {
      await save.mutateAsync({ ruleId: editing?.id, data });
      message.success(editing ? 'Đã cập nhật quy tắc' : 'Đã tạo quy tắc');
      setOpen(false);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const columns = [
    { title: 'Tên', dataIndex: 'name', key: 'name' },
    {
      title: 'Mã trạng thái',
      dataIndex: 'matchStatus',
      key: 'matchStatus',
      render: (v: number[]) => (v ?? []).join(', ') || '—',
    },
    {
      title: 'Body chứa',
      dataIndex: 'matchBodyContains',
      key: 'matchBodyContains',
      render: (v: string) => v || '—',
    },
    {
      title: 'Phân loại',
      dataIndex: 'classify',
      key: 'classify',
      render: (v: ProviderErrorRule['classify']) => <ErrorClassTag classify={v} />,
    },
    {
      title: 'Tạo lại',
      dataIndex: 'noRetry',
      key: 'noRetry',
      width: 130,
      render: (v: boolean) =>
        v ? <Tag color="red">Bỏ qua tạo lại</Tag> : <Tag color="green">Theo cấu hình nguồn</Tag>,
    },
    { title: 'Ưu tiên', dataIndex: 'priority', key: 'priority', width: 90 },
    {
      title: 'Bật',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 70,
      render: (v: boolean) => (v ? 'Có' : 'Không'),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_: unknown, r: ProviderErrorRule) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="Xóa quy tắc?" okText="Xóa" cancelText="Hủy" onConfirm={() => del.mutate(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Thêm quy tắc
        </Button>
      </Space>
      <Table<ProviderErrorRule>
        rowKey="id"
        size="small"
        loading={rules.isLoading}
        dataSource={(rules.data ?? []).slice().sort((a, b) => a.priority - b.priority)}
        columns={columns}
        pagination={false}
      />

      <Drawer
        title={editing ? 'Sửa quy tắc lỗi' : 'Thêm quy tắc lỗi'}
        width={480}
        open={open}
        onClose={() => setOpen(false)}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={() => setOpen(false)}>Hủy</Button>
            <Button type="primary" loading={save.isPending} onClick={onSubmit}>
              Lưu
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Tên" rules={[{ required: true, message: 'Vui lòng nhập tên' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="matchStatus"
            label="Mã trạng thái khớp (phân cách dấu phẩy)"
            tooltip="Vd: 429, 503"
          >
            <Input placeholder="429, 503" />
          </Form.Item>
          <Form.Item name="matchBodyContains" label="Body chứa chuỗi">
            <Input />
          </Form.Item>
          <Form.Item name="classify" label="Phân loại">
            <Select options={CLASSIFY_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="noRetry"
            label="Bỏ qua tạo lại"
            valuePropName="checked"
            tooltip="Bật: lỗi khớp quy tắc này KHÔNG được tạo lại, dù nguồn có cấu hình 'Số lần tạo lại khi lỗi'. Dùng cho lỗi do chính đầu vào (vd Dola 602 — ảnh người thật), tạo lại vẫn hỏng."
          >
            <Switch />
          </Form.Item>
          <Form.Item name="priority" label="Ưu tiên">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="enabled" label="Bật" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}
